<template>
    <div data-component="CartOffersCodeSearch"
         class="cart-offers-code-search offer-selector">
        <template v-if="hasApplyOffersPermission">
            <slot></slot>

            <div class="check-coupon-form">
                <form @submit.prevent="search()">
                    <div class="input-group-flex">
                        <input ref="input"
                               class="form-control input-xl"
                               data-testid="offer-codes-input"
                               :placeholder="inputPlaceholder"
                               :value="ref"
                               :disabled="!isOnline"
                               @input="onInput" />
                        <SquareBtn color="primary"
                                   :disabled="!isOnline || !ref"
                                   data-testid="offer-codes-search-btn"
                                   size="xl"
                                   class="btn--inline-flex"
                                   :loading="searching"
                                   @click="search">
                            {{ l('CART_OFFERS.COUPON_SEARCH') }}
                        </SquareBtn>
                        <SquareBtn :disabled="!ref"
                                   data-testid="offer-codes-clear-btn"
                                   size="xl"
                                   class="btn--inline-flex btn-white"
                                   :loading="searching"
                                   @click="reset">
                            {{ l('CLEAR') }}
                        </SquareBtn>
                    </div>
                </form>

                <div v-if="offerFromApi === false"
                     class="alert alert-danger mt-1">
                    <i class="fa-solid fa-circle-xmark"></i> {{ l('CART_OFFERS.COUPON_SEARCH_NO_RESULTS', { ref }) }}
                </div>
            </div>

            <!-- Offers list -->
            <div v-if="offers && offers.length > 0 && ref"
                 class="btn-grid selectable-btns offer-selector__offers mt-1">
                <OfferButton v-for="offer in offers"
                             :key="offer.ID"
                             :offer="offer"
                             :selected="selectedOfferIDs.includes(offer.ID) && selectedOfferCodes[offer.ID] === offer.code"
                             :selected-count="selectedOffers[offer.ID]"
                             :disabled="disabledOfferIds.includes(offer.ID)"
                             @select="selectOffer"
                             @deselect="deselectOffer" />
            </div>
        </template>
        <!-- Strict type check because null means the permission is still being checked. -->
        <template v-else-if="hasApplyOffersPermission === false">
            <div class="alert alert-danger">
                {{ l('CART_OFFERS.NO_PERMISSION') }}
            </div>
        </template>
        <PageLoadingSpinner v-else />
    </div>
</template>

<script>
import OfferButton from './OfferButton';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    name: 'CartOffersCodeSearch',

    components: {
        OfferButton,
        PageLoadingSpinner,
        SquareBtn,
    },

    props: {
        offers: {
            type: Array,
            default: () => [],
        },

        /**
         * The offer(s) that the operator has selected.
         *
         * The keys will be offerIDs.
         * Some offers can be applied more than once depending on how many time it is clicked.
         * In that case the value will be a number >= 1, otherwise the value will be 1.
         * e.g. { 37: 1 }
         *
         * @type {Object<number, number>}
         */
        initialSelectedOffers: {
            type: Object,
            default: () => {},
        },

        /**
         * The offer codes that the operator has validated.
         * The keys will be offersIDs (although at the moment there will only ever be one)
         * The value will be the corresponding offer code.
         * e.g. { 22: 'JAN-2025' }
         *
         * @type {Object<number, string>}
         */
        initialSelectedOfferCodes: {
            type: Object,
            default: () => {},
        },

        /**
         * Any offers where their ID appears in this array will be displayed to the user,
         * but as disabled buttons, so they can't be selected.
         *
         * @type {number[]}
         */
        disabledOfferIds: {
            type: Array,
            default: () => [],
        },

        allowMultiple: {
            type: Boolean,
            default: false,
        },

        allowOfferSelection: {
            type: Boolean,
            default: true,
        },
    },

    emits: [
        'update:selectedOfferCodes',
        'update:selectedOffers',
    ],

    data() {
        return {
            searching: false,

            /**
             * @type {string}
             */
            ref: '',

            /**
             * Does the current user has permission to apply offers?
             * We won't show anything if they don't.
             */
            hasApplyOffersPermission: null,

            /**
             * Does the current user has permission to apply supervisor-only offers?
             * We will show those offers but greyed out if they don't.
             */
            hasApplySupervisorOfferPermission: null,

            searchQuery: '',

            /**
             * An offer returned from a request to the API using a coupon code.
             *
             * @type {Offer}
             */
            offerFromApi: null,

            /**
             * This is a clone of the initialSelectedOffers passed in, so we can modify it.
             * Any changes to this should be emitted as update:selectedOffers.
             * See props.initialSelectedOffers.
             *
             * @type {Object<number, number>}
             */
            selectedOffers: Object.assign({}, this.initialSelectedOffers),

            /**
             * This is a clone of the initialSelectedOfferCodes passed in, so we can modify it.
             * Any changes to this should be emitted as update:selectedOfferCodes.
             * See props.initialSelectedOfferCodes.
             *
             * @type {Object<number, string>}
             */
            selectedOfferCodes: Object.assign({}, this.initialSelectedOfferCodes),
        };
    },

    computed: {
        ...mapState(['isOnline']),

        inputPlaceholder() {
            return container.langService.getText('CART_OFFERS.COUPON_SEARCH_PLACEHOLDER');
        },

        selectedOfferIDs() {
            return Object.keys(this.selectedOffers).map((id) => parseInt(id));
        },
    },

    created() {
        container.privilegesManager
            .requirePrivilege(PrivilegeNames.APPLY_DISCOUNTS)
            .then((result) => {
                if (result.granted) {
                    this.hasApplyOffersPermission = true;
                    this.$nextTick(() => {
                        this.$refs.input.focus();
                    });

                    // Check if the user that granted the apply offers permission also has
                    // the apply_supervisor_discounts permission.
                    // Note that `grantedBy` could also be the current user if that user already had the permission.
                    container.privilegesManager
                        .checkUserPrivilege(
                            result.grantedBy,
                            PrivilegeNames.APPLY_SUPERVISOR_DISCOUNTS
                        )
                        .then(() => {
                            this.hasApplySupervisorOfferPermission = true;
                        })
                        .catch(() => {
                            this.hasApplySupervisorOfferPermission = false;
                        });
                }
            })
            .catch((e) => {
                if (e) {
                    console.warn(e);
                }
                this.hasApplyOffersPermission = false;
            });
    },

    methods: {
        /**
         * @param {Offer} offer
         */
        async selectOffer(offer) {
            if (!this.allowOfferSelection) {
                return;
            }

            if (offer.requiresSupervisorApproval) {
                try {
                    await this.checkForSupervisorOffersPermission();
                } catch (e) {
                    console.error('User does not have permission to apply supervisor offers.');
                    return;
                }
            }

            if (!this.allowMultiple) {
                // If selecting multiple offers is not allowed, remove all other selected offers
                // when selecting a new one. This will also clear any variable discount data for other offers.
                Object.keys(this.selectedOffers)
                    .map(parseInt)
                    .filter((offerID) => offerID !== offer.ID)
                    .forEach((offerID) => this.deselectOfferByID(offerID));
            }

            if (offer.applyPerTriggerLimit && this.selectedOfferIDs.includes(offer.ID)) {
                // If the offer supports being selected multiple times add another selection.
                ++this.selectedOffers[offer.ID];
            } else {
                // Otherwise select the offer 1 time.
                this.selectedOffers[offer.ID] = 1;
            }
            this.selectedOfferCodes[offer.ID] = offer.code;

            this.$emit('update:selectedOffers', this.selectedOffers);
            this.$emit('update:selectedOfferCodes', this.selectedOfferCodes);
        },

        /**
         * @param {Offer} offer
         */
        async deselectOffer(offer) {
            delete this.selectedOffers[offer.ID];
            delete this.selectedOfferCodes[offer.ID];

            this.$emit('update:selectedOffers', this.selectedOffers);
            this.$emit('update:selectedOfferCodes', this.selectedOfferCodes);
        },

        /**
         * @param {number} offerID
         */
        async deselectOfferByID(offerID) {
            const offer = this.offers.find((o) => o.ID === offerID);
            if (!offer) {
                return Promise.reject();
            }

            return this.deselectOffer(offer);
        },

        /**
         * @return {Promise<boolean>}
         */
        async checkForSupervisorOffersPermission() {
            if (this.hasApplySupervisorOfferPermission) {
                return true;
            }

            return container.privilegesManager
                .requirePrivilege(PrivilegeNames.APPLY_SUPERVISOR_DISCOUNTS)
                .then((result) => {
                    if (result.granted) {
                        this.hasApplySupervisorOfferPermission = true;
                    }
                    return true;
                });
        },

        /**
         * @param {InputEvent} evt
         */
        onInput(evt) {
            this.offerFromApi = null;
            this.ref = evt.target.value;
        },

        search() {
            this.searching = true;

            container.offerService
                .remoteFindOfferByCode(this.ref)
                .then((offer) => {
                    this.offerFromApi = offer || false;

                    if (offer && !this.offers.some((o) => o.ID === offer.ID && o.code === offer.code)) {
                        this.offers.push(offer);
                        this.selectOffer(offer);
                    }
                })
                .finally(() => {
                    this.searching = false;
                });
        },

        reset() {
            this.offerFromApi = null;
            this.ref = '';
            this.searchQuery = '';
            this.$refs.input.focus();
        },
    },
};
</script>
